import { graphql } from 'client/gql';
import { GroupType } from 'client/gql/graphql';
import { useGroupsTable } from 'components/Groups/hooks/useGroupsTable';
import { GroupEditorsModal } from 'components/Groups/modals/GroupEditorsModal';
import { GroupModal } from 'components/Groups/modals/GroupModal';
import { GroupSettingsModal } from 'components/Groups/modals/GroupSettingsModal';
import { GroupViewersModal } from 'components/Groups/modals/GroupViewersModal';
import { InstitutionGroupsTable } from 'components/Groups/tables/InstitutionGroupsTable';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { updateClaims } from '../../../utils/authStore';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupFromInstitutionGroups($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

export type GroupSettings = {
  cloudDirectory: boolean;
  asThreemaGroup: boolean;
  allowViewersCanAddMaterial: boolean;
  allowViewersCanCreateEvents: boolean;
  allowViewersCanStartMeeting: boolean;
};

const InstitutionGroupsSubpageQueryDocument = graphql(`
  query InstitutionGroupsSubpageData(
    $groupWhere: GroupWhere!
    $calendarEventWhere: CalendarEventWhere!
    $organizationUuid: ID!
    $groupUuid: ID!
  ) {
    ...UseGroupsTableFields
    ...GroupEditorsFormFields
    ...GroupViewersFormFields
  }
`);

export const InstitutionGroupsSubpage = () => {
  const navigate = useNavigate();
  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [, deleteGroup] = useMutation(DeleteGroupMutationDocument);

  const { ConfirmationDialog, confirmDelete } = useGroupsTable();

  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear.uuid);
  const [showSpanning, setShowSpanning] = useState<boolean>(true);
  const [groupUuid, setGroupUuid] = useState<string | null>(null);
  const [groupModalOpen, setGroupModalOpen] = useState<boolean>(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const [editorsModalOpen, setEditorsModalOpen] = useState<boolean>(false);
  const [viewersModalOpen, setViewersModalOpen] = useState<boolean>(false);

  const [{ data }] = useQuery({
    query: InstitutionGroupsSubpageQueryDocument,
    variables: {
      organizationUuid: organization.uuid,
      schoolYearUuid: selectedYearUuid,
      groupUuid: groupUuid ?? '',
      groupWhere: {
        type: GroupType.Group,
        organization: { uuid: organization.uuid },
        AND: [
          {
            OR: [{ schoolYear: { uuid: selectedYearUuid } }, { schoolYear: showSpanning ? null : undefined }],
          },
        ],
      },
      calendarEventWhere: {
        holderConnection: {
          Group: {
            node: {
              organization: { uuid: organization.uuid },
              type: GroupType.Group,
              AND: [
                {
                  OR: [{ schoolYear: { uuid: selectedYearUuid } }, { schoolYear: showSpanning ? null : undefined }],
                },
              ],
            },
          },
        },
        virtualLocations_SOME: {
          running: true,
        },
      },
    },
  });
  const { t } = useTranslation();
  const permissions = usePermissionChecker();

  async function handleDelete(uuid: string, name: string) {
    const result = await confirmDelete({
      title: t('groups.delete.title'),
      message: t('groups.delete.message', { name }),
      confirmText: t('groups.delete.confirmText'),
    });
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteGroup({ id: uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  }

  function handleSettings(uuid: string) {
    setGroupUuid(uuid);
    setSettingsModalOpen(true);
  }

  function handleToggleSpanning() {
    setShowSpanning(!showSpanning);
  }

  function handleAdd() {
    setGroupUuid(null);
    setGroupModalOpen(true);
  }

  function handleEdit(uuid: string) {
    setGroupUuid(uuid);
    setGroupModalOpen(true);
  }

  async function handleClose(withClaims: boolean) {
    setGroupModalOpen(false);
    setSettingsModalOpen(false);
    setEditorsModalOpen(false);
    setViewersModalOpen(false);
    setGroupUuid(null);
    if (withClaims) {
      await updateClaims(() => setClaimsTrigger(!claimsTrigger));
    }
  }

  return (
    <BpSubpage isForbidden={!permissions?.canListInstitutionGroups(organization) || !organization}>
      <InstitutionGroupsTable
        query={data}
        permissions={permissions}
        loading={tableLoading}
        yearUuid={selectedYearUuid}
        showSpanning={showSpanning}
        onYearSelect={setSelectedYearUuid}
        onToggleSpanning={handleToggleSpanning}
        onNavigate={(uuid) => navigate('/groups/' + uuid)}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSettings={(uuid) => handleSettings(uuid)}
      />

      <GroupModal
        isOpen={groupModalOpen}
        groupUuid={groupUuid}
        schoolYearUuid={selectedYearUuid}
        page='institution'
        onClose={() => handleClose(true)}
      />

      {groupUuid && (
        <GroupEditorsModal
          isOpen={editorsModalOpen}
          groupUuid={groupUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleClose(true)}
          query={data}
        />
      )}

      {groupUuid && (
        <GroupViewersModal
          isOpen={viewersModalOpen}
          groupUuid={groupUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleClose(true)}
          query={data}
        />
      )}

      {groupUuid && (
        <GroupSettingsModal isOpen={settingsModalOpen} groupUuid={groupUuid} onClose={() => handleClose(false)} />
      )}

      <ConfirmationDialog />
    </BpSubpage>
  );
};
